import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, X, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const GallerySection = () => {
  const { lang } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const intervalRef = useRef(null);

  const texts = {
    de: { title: 'Unsere Gemeinde in Bildern', subtitle: 'Einblicke in unser lebendiges Gemeindeleben \u2013 Gottesdienste, Feste und besondere Momente.' },
    en: { title: 'Our Community in Pictures', subtitle: 'Glimpses into our vibrant church life \u2013 services, celebrations and special moments.' },
    fr: { title: 'Notre communaute en images', subtitle: 'Apercu de notre vie communautaire dynamique \u2013 cultes, fetes et moments speciaux.' },
  };
  const t = texts[lang] || texts.de;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/gallery`);
        const data = await res.json();
        if (data && data.length > 0) setPhotos(data);
      } catch (err) {
        console.error('Error loading gallery:', err);
      }
    };
    load();
  }, []);

  // Auto-slideshow
  const startSlideshow = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % Math.max(photos.length, 1));
    }, 4000);
  }, [photos.length]);

  useEffect(() => {
    if (isPlaying && photos.length > 1) {
      startSlideshow();
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, photos.length, startSlideshow]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (isPlaying) startSlideshow(); // Reset timer
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + photos.length) % photos.length);
    if (isPlaying) startSlideshow();
  };

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % photos.length);
    if (isPlaying) startSlideshow();
  };

  if (photos.length === 0) return null;

  // Show featured slideshow (first 8 photos) + grid of remaining
  const slideshowPhotos = photos.slice(0, 8);
  const gridPhotos = photos.slice(0, 8);

  return (
    <section className="py-20 bg-gray-50" id="gallery" data-testid="gallery-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">{t.title}</h2>
          <div className="w-20 h-1 bg-[#0f2440] mx-auto mb-6"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Slideshow */}
        {slideshowPhotos.length > 1 && (
          <div className="relative max-w-4xl mx-auto mb-10 rounded-2xl overflow-hidden shadow-xl group" data-testid="gallery-slideshow">
            <div className="relative aspect-[16/9] bg-gray-900">
              {slideshowPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={`${API_BASE}/gallery/image/${photo.id}`}
                    alt={photo.caption || 'Gemeinde-Foto'}
                    className="w-full h-full object-cover"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                  {/* Caption overlay */}
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6 pt-16">
                      <p className="text-white font-medium text-lg">{photo.caption}</p>
                      {photo.category && (
                        <span className="text-white/70 text-sm">{photo.category}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                data-testid="gallery-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextSlide}
                data-testid="gallery-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Play/Pause Button */}
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                data-testid="gallery-playpause"
                className="absolute top-3 right-3 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>

              {/* Dots */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {slideshowPhotos.map((photo, index) => (
                  <button
                    key={photo.id || index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide
                        ? 'bg-white w-6'
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Grid Thumbnails */}
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 max-w-4xl mx-auto">
          {gridPhotos.map((photo, index) => (
            <button
              key={photo.id}
              onClick={() => setSelectedPhoto(photo)}
              data-testid={`gallery-item-${photo.id}`}
              className={`group relative aspect-square rounded-lg overflow-hidden transition-all cursor-pointer ${
                index === currentSlide ? 'ring-2 ring-[#0f2440] ring-offset-2' : 'opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={`${API_BASE}/gallery/image/${photo.id}`}
                alt={photo.caption || 'Gemeinde-Foto'}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}>
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={`${API_BASE}/gallery/image/${selectedPhoto.id}`}
            alt={selectedPhoto.caption || 'Foto'}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedPhoto.caption && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm">
              {selectedPhoto.caption}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default GallerySection;
